import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "product-media-overview" }
const _hoisted_2 = { class: "product-media-overview__breadcrumbs" }

import { computed, inject, onMounted, provide, ref, watch } from 'vue';

import { pageKey, routeKey } from '@drapejs/core';

import { useCartFunctions } from '@/composables/useCartFunctions';
import { channelKey } from '@/keys';

import ProductOverviewDetails from './ProductOverviewDetails.vue';
import ProductOverviewMedia from './ProductOverviewMedia.vue';
import Breadcrumbs from '@/components/Breadcrumbs.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductOverview',
  setup(__props) {

const modelImageRegex = /_m(\d{3})/;

const route = inject(routeKey);
const page = inject(pageKey);

const cartFunctions = useCartFunctions();

const selectedArticleNumber = ref<string | null>(null);

const product = computed(() => page?.value.dataJson?.product);
const productHasStock = computed(() => {
  return !product.value?.otherVariants.every((v: any) => !v.inStock);
});

const selectedSize = computed(() => {
  if (!selectedVariant.value) return null;

  return (
    product.value.otherVariants.filter(
      (s: any) => s.systemId === selectedVariant.value!.systemId
    )[0] || null
  );
});

const selectedVariant = computed(() => {
  if (!selectedArticleNumber.value) return null;

  const v = product.value?.otherVariants.filter(
    (s: any) => s.articleNumber === selectedArticleNumber.value
  )[0];
  return v || null;
});

const selectedSizeHasStock = computed(() => {
  return selectedSize.value?.inStock || false;
});

const isComingSoon = computed(() => {
  return !productHasStock.value && product.value.showAsComingSoon;
});

const purchaseDecisionInfo = computed(
  () => product.value?.purchaseDecisionInfo
);

const sizes = computed(() => product.value?.otherVariants);
const hasSizes = computed(() => (sizes.value || []).length > 0);
const channel = inject(channelKey);
const websiteText = computed(() => channel?.value?.website?.texts || {});

const outOfStockButtonLabel = computed(() => {
  return isComingSoon.value
    ? websiteText.value.addtocart__coming_soon || 'addtocart__coming_soon'
    : websiteText.value.productdetails_soldout || 'productdetails_soldout';
});

const productBreadCrumbs = computed(() => {
  const category = page?.value?.dataJson?.category;
  const product = page?.value?.dataJson?.product;

  return {
    parents: [
      ...(category?.parents?.filter((p: any) => p.url && p.name) || []),
      {
        name: category?.name,
        url: category?.url,
      },
    ],
    name: product?.fields?._name,
    url: `${category?.url}/${product?.fields?._url}`,
  };
});

provide('addToCartContext', {
  selectVariant,
  selectedVariant,
  addSelectedToCart,
  purchaseDecisionInfo,
  selectedSizeHasStock,
  outOfStockButtonLabel,
  product,
  productHasStock,
  sizes,
  hasSizes,
  selectedSize,
});

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  watch(
    route!,
    () => {
      selectFirstAvailableSize();
    },
    { deep: true }
  );

  selectFirstAvailableSize();
});

function selectFirstAvailableSize() {
  const v = product.value?.otherVariants.filter((s: any) => s.inStock)[0];
  if (v) {
    selectVariant(v);
  } else {
    selectVariant(product.value?.otherVariants[0] || null);
  }
}

function selectVariant(v: any) {
  selectedArticleNumber.value = v?.articleNumber || null;
}

async function addSelectedToCart() {
  const s = selectedVariant.value;
  if (!s) return;

  await cartFunctions.addItemToCart({
    articleNumber: s.articleNumber,
    quantity: 1,
  });

  cartFunctions.isCartOpen.value = true;
}

const canDisplayProductImagesOverview = computed(
  () =>
    selectedVariant.value?.images?.some((i: { id: string; filename: string }) =>
      i.filename.match(modelImageRegex)
    ) || false
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(Breadcrumbs, { category: _unref(productBreadCrumbs) }, null, 8, ["category"])
    ]),
    _createVNode(ProductOverviewMedia, {
      class: "product-media-overview__media",
      images: 
        _unref(selectedVariant)?.images?.map((i) => i.id) || _unref(product)?.images || []
      ,
      videos: _unref(product)?.videos || [],
      canDisplayProductImagesOverview: _unref(canDisplayProductImagesOverview),
      alt: _unref(product).fields._name
    }, null, 8, ["images", "videos", "canDisplayProductImagesOverview", "alt"]),
    _createVNode(ProductOverviewDetails, {
      class: "product-media-overview__details",
      product: _unref(product)
    }, null, 8, ["product"])
  ]))
}
}

})